import type { NavButtonFontSchemaType, NavButtonSchemaType } from '@vedalib/editor/lib/brand'
import type { ButtonElementValue } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import lodash from 'lodash'
import { useCallback } from 'react'

import type { Properties } from 'components/Editor/EditorElements/elements.types'
import type { ButtonElementType } from 'components/Editor/EditorElements/elements/Button/buttonTypes'
import { useElementRootStyles } from 'components/Editor/EditorElements/hooks/useElementRootStyles'
import type { NavButtonStyles } from 'components/Editor/cource/controls/LandingFooterNavigation/helpers'
import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getIsInteractiveMode } from 'services/Store/Project/selectors'
import { testProps, testPropsEl } from 'utils/test/qaData'

import * as s from './BaseNavigationButton.module.scss'
import BaseNavigationButtonStyled from './BaseNavigationButtonStyled'

interface IBaseButtonElementProps extends Pick<Properties<ButtonElementType>, 'mode'> {
  label?: string
  disabled?: boolean
  onClick?: () => void
  icon?: React.ReactElement | null
  action?: ButtonElementValue['action']
  fonts?: ElementFontCss<NavButtonFontSchemaType>
  styles: {
    root?: ElementStyleCss<NavButtonSchemaType>['root']
    button: NavButtonStyles
    hover: { backgroundColor?: string }
    effects: { boxShadow?: string }
  }
  iconPosition: 'left' | 'right'
}

const BaseNavigationButton: React.FC<IBaseButtonElementProps> = ({
  label,
  disabled: disabledProp,
  styles,
  onClick,
  icon,
  action,
  fonts,
  iconPosition,
}) => {
  const rootStyles = useElementRootStyles(styles.root)
  const isInteractive = useProjectContext(getIsInteractiveMode)
  const disabled = !isInteractive || disabledProp
  const rootCn = cn(s.root, { [s.disabled]: disabled })

  const onClickHandler = useCallback(() => {
    if (disabled) {
      return
    }

    onClick?.()
  }, [disabled, onClick])

  return (
    <BaseNavigationButtonStyled
      {...testPropsEl('courseButton', { value: action }, { label, disabled })}
      $button={styles.button}
      $hover={styles.hover}
      className={rootCn}
      onClick={onClickHandler}
      style={{
        ...rootStyles,
        ...lodash.omit(styles.button, ['backgroundColor']),
        ...fonts?.base,
        ...styles.effects,
      }}
    >
      {styles.button?.showIcons && iconPosition === 'left' && (
        <div
          className={s.iconLeft}
          {...testProps({
            el: 'iconCourseButton',
            position: 'left',
            value: icon?.props?.name,
          })}
        >
          {icon}
        </div>
      )}
      {label}
      {styles.button?.showIcons && iconPosition === 'right' && (
        <div
          className={s.iconRight}
          {...testProps({
            el: 'iconCourseButton',
            position: 'right',
            value: icon?.props?.name,
          })}
        >
          {icon}
        </div>
      )}
    </BaseNavigationButtonStyled>
  )
}

export default BaseNavigationButton
